/**
 * @file localization-mixin.ts
 *
 * Owns the LocalizationController instance and exposes language-related
 * helpers. Instantiates the controller in the constructor (so lang-file
 * fetching starts before the element connects) and attaches event listeners
 * in connectedCallback.
 *
 * Exposes: _loc, updateLangSubscribers
 */
import sharedLocalisationDict from '../../../data/sharedLocalisation.json';
import defaultTranslationsDict from '../../../data/defaultTranslations.json';
import { fetchResource } from '../../../features/serverApi/asApi.ts';
import { type Constructor } from './mixin-types-utils.ts';
import { internalData } from '../../../store/store.ts';
import { LANG_STORAGE_KEY, SHOW_LANG_CTRLS } from '../../../data/constants.ts';
import { interpolateText } from '../../text-and-lang/interpolate.ts';
import { sendEv } from '../../../data/events.ts';

// ============================================================================
// Localization constants
// ============================================================================

export const langAbbrev = {
  "en": "English",
  "de": "Deutsch",
  "es": "Español",
  "fr": "Français",
  "jp": "日本語",
  "cn": "中文",
  "it": "Italiano",
  "ko": "한국어",
  "pt": "Português",
  "ukr": "Українська",
  "dk": "Dansk",
  "nl": "Nederlands"
} as const;

const defaultTranslations: { [lang: string]: { [key: string]: string } } = defaultTranslationsDict as any;

// ============================================================================
// Localization controller
// ============================================================================

// Keeps track of the current language and all the elements that want
// text updates when it changes.
class LocalizationController {
  // Elements waiting for a specific text key, grouped by that key.
  private _textSubscribers: Map<string, Set<HTMLElement>> = new Map();
  // Variables to fill into a subscriber's text, e.g. {0} in "Hello {0}".
  private _subscriberVars: WeakMap<HTMLElement, any[]> = new WeakMap();
  // Translations loaded from the app's own langFile attribute, if any.
  private _langText: { [lang: string]: { [key: string]: string } } | null = null;
  // Translations shared across all apps, bundled at build time.
  private readonly _sharedLocalisation: { [lang: string]: { [key: string]: string } } = sharedLocalisationDict as any;

  // internalData.language is the single source of truth — anything that needs
  // the active language (e.g. as-lang-selector) reads/subscribes there
  // instead of keeping its own copy synced by hand. This getter/setter is
  // just a convenience so the rest of this class can keep saying
  // `this.language` instead of `internalData.language` everywhere.
  get language(): string { return internalData.language; }
  set language(lang: string) { internalData.language = lang; }

  constructor(private readonly _host: HTMLElement, initialLang: string = 'en') {
    // Keep the host's `lang` attribute reflecting internalData.language
    // reactively, same as anything else that cares about the active
    // language — registered once, up front, so it's live for every write
    // to `language` from here on, not just the ones that go through
    // updateLang().
    internalData.subscribeKey('language', (lang: string) => this._syncLangAttr(lang));
    this.language = initialLang;
  }

  // Loads the app-specific language file named by the langFile attribute,
  // if one is set, and pushes fresh text out once it arrives.
  fetchLangFiles(): void {
    const src = this._host.getAttribute('langFile') ?? '';
    if (!src) return;
    fetchResource(src)
      .then(data => {
        this._langText = data;
        this.updateLangSubscribers();
      })
      .catch(err => {
        console.error('Error loading language file:', err);
      });
  }

  // True while a updateLang() call is running its own side effects. The
  // `language` side effect registered in the constructor sets the host's
  // `lang` attribute, and as-app routes attributeChangedCallback('lang', ...)
  // straight back into updateLang() — without this guard that attribute
  // write would re-enter and re-run the whole update a second time.
  private _applyingLang = false;

  // Switches the active language, tells every subscriber, and fetches
  // the language file if it hasn't been loaded yet.
  updateLang(lang: string): void {
    if (this._applyingLang) return;
    this._applyingLang = true;
    try {
      this.language = lang;
      this.updateLangSubscribers();
      sendEv(this._host, 'as-language-changed', {
        detail: { lang }
      });
      if (!this._langText) {
        this.fetchLangFiles();
      }
      this._syncLangToURL(lang);
      this._persistLang(lang);
    } finally {
      this._applyingLang = false;
    }
  }

  // Keeps the host's `lang` attribute in step with internalData.language,
  // whichever code path changed it — including ones that never call
  // updateLang() at all. Registered as a store side effect (see
  // constructor) rather than called inline, so it can't be forgotten by a
  // future writer of `language`. Same no-op-if-already-equal shape as
  // _syncLangToURL below, but for the DOM attribute.
  private _syncLangAttr(lang: string): void {
    if (this._host.getAttribute('lang') !== lang) {
      this._host.setAttribute('lang', lang);
    }
  }

  // Remembers the language the user is actually on so the next bare launch
  // (no ?lang=, no explicit `lang` attribute) can pick it up again — see
  // as-app's _initLang. Best-effort: localStorage can throw in sandboxed
  // iframes (e.g. as-code-demo), so a failure here is silently ignored.
  private _persistLang(lang: string): void {
    try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch { /* sandboxed iframe, etc */ }
  }

  // Keeps `?lang=` in the address bar in step with the active language, so a
  // language switch produces a shareable/bookmarkable/reloadable URL — the
  // other half of the URL→app direction handled by as-app's _initLangFromURL.
  // replaceState (not pushState): a language switch isn't a navigation, so it
  // shouldn't add a back-button stop. No-ops if the URL already agrees, which
  // also keeps _initLangFromURL's own initial updateLang() call from writing
  // straight back to the URL it just read from.
  private _syncLangToURL(lang: string): void {
    // Production hides all language controls, including this URL write-back —
    // see SHOW_LANG_CTRLS in constants.ts. Reading ?lang= is unaffected.
    if (!SHOW_LANG_CTRLS) return;
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('lang') === lang) return;
    url.searchParams.set('lang', lang);
    window.history.replaceState(window.history.state, '', url);
  }

  // Re-sends text to every subscriber, for every key they asked about.
  updateLangSubscribers(): void {
    this._textSubscribers.forEach((subscribers, textKey) => {
      subscribers.forEach(subscriber => {
        this.sendTextToSubscriber(textKey, subscriber);
      });
    });
  }

  // Looks up a text key for the current language. Checks shared text
  // first, then the app's own language file, then the built-in defaults.
  getTextString(textKey: string): string | null {
    const lang = this.language;

    if (this._sharedLocalisation?.[lang]?.[textKey]) {
      return this._sharedLocalisation[lang][textKey];
    }
    if (this._langText?.[lang]?.[textKey]) {
      return this._langText[lang][textKey];
    }
    if (defaultTranslations[lang]?.[textKey]) {
      return defaultTranslations[lang][textKey];
    }
    if (textKey.startsWith('tooltip.')) {
      const simplifiedKey = textKey.substring(textKey.lastIndexOf('.') + 1);
      return simplifiedKey.charAt(0).toUpperCase() + simplifiedKey.slice(1);
    }

    return null;
  }

  // Finds the text for a key and fires it at one subscriber as an event.
  sendTextToSubscriber(textKey: string, subscriber: HTMLElement): boolean {
    let text = this.getTextString(textKey);
    if (text === null) {
      // No translation anywhere for this key in this language. Fall back to
      // the subscriber's own default-text instead of no-oping — a no-op here
      // left whatever the previous language's text happened to be on screen,
      // which reads as a stuck/untranslated element rather than a fallback.
      text = subscriber.getAttribute?.('default-text') ?? '';
    }

    // {N} placeholders are hidden (replaced with '') unless the subscriber
    // opts out with a `raw` attribute, in which case they're left literal.
    if (!subscriber.hasAttribute?.('raw')) {
      const vars = this._subscriberVars.get(subscriber) || [];
      text = interpolateText(text, vars, (dictKey) => this.getTextString(dictKey));
    }

    sendEv(subscriber, 'as-local-text-update', {
      bubbles: false,
      detail: { text }
    });
    return true;
  }

  /**
   * Register all localization event listeners on the host element.
   * Called once from the mixin constructor (not connectedCallback — see the
   * comment there for why).
   */
  attachListeners(): void {
    this._host.addEventListener('as-local-text-subscribe', (event: CustomEvent) => {
      const { textKey, requester, vars } = event.detail;

      if (vars !== undefined) {
        this._subscriberVars.set(requester, vars);
      } else {
        this._subscriberVars.delete(requester);
      }

      if (!this._textSubscribers.has(textKey)) {
        this._textSubscribers.set(textKey, new Set());
      }
      this._textSubscribers.get(textKey)!.add(requester);
      this.sendTextToSubscriber(textKey, requester);
    });

    this._host.addEventListener('as-local-text-unsubscribe', (event: CustomEvent) => {
      const { textKey, requester } = event.detail;
      const subscribers = this._textSubscribers.get(textKey);
      if (subscribers) {
        subscribers.delete(requester);
        if (subscribers.size === 0) {
          this._textSubscribers.delete(textKey);
        }
      }
    });

    this._host.addEventListener('as-get-current-language', (event: CustomEvent) => {
      if (event.detail?.requester) {
        sendEv(event.detail.requester, 'as-current-language', {
          bubbles: false,
          detail: { language: this.language }
        });
      }
    });

    this._host.addEventListener('as-get-init-lang', (event: CustomEvent) => {
      if (event.detail?.requester) {
        if (typeof event.detail.requester.setLanguage === 'function') {
          event.detail.requester.setLanguage(this.language);
        }
      }
    });

    this._host.addEventListener('as-change-language', (event: CustomEvent) => {
      this.updateLang(event.detail.lang);
    });
  }
}

// ============================================================================
// Mixin
// ============================================================================

/** Surface added by LocalizationMixin. Used by nothing currently — kept for
 *  future mixins that need to trigger language updates. */
export interface LocalizationMixinMembers {
  _loc: LocalizationController;
  updateLangSubscribers(): void;
}

export const LocalizationMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class LocalizationMixin extends Base {
    _loc: LocalizationController;

    constructor(...args: any[]) {
      super(...args);
      // Create the controller early so lang-file loading starts right
      // away, before the element is even attached to the page. Listeners are
      // wired up here too (not connectedCallback): addEventListener doesn't
      // need the element connected, and a descendant's as-text can dispatch
      // its 'as-local-text-subscribe' the instant it resolves the app
      // reference (see as-app's as-get-app responder, registered the same
      // way) — if that now-instant resolution outran attachListeners() sitting
      // behind connectedCallback's async mixin-chain await, the subscribe
      // event would arrive before anything was listening for it and be lost
      // with no retry.
      this._loc = new LocalizationController(this, this.getAttribute('lang') ?? 'en');
      this._loc.attachListeners();
      this._loc.fetchLangFiles();
    }

    updateLangSubscribers(): void { this._loc.updateLangSubscribers(); }
  };
